import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { ArrowLeft, Lock } from 'lucide-react';
import { apiRequest } from './App';

const ChangePassword = ({ user }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    
    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 6) {
      newErrors.newPassword = 'New password must be at least 6 characters';
    }
    
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (currentPassword && newPassword && currentPassword === newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await apiRequest('/api/change-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      if (response.ok) {
        setSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setErrors({});
        
        // Show success message and redirect after delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401) {
          setErrors({ currentPassword: errorData.message || 'Current password is incorrect' });
        } else {
          setErrors({ general: errorData.message || 'Failed to change password' });
        }
      }
    } catch (error) {
      console.error('Change password error:', error);
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    setSuccess(false);
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          component={Link}
          to="/dashboard"
          variant="outlined"
          startIcon={<ArrowLeft size={16} />}
        >
          Back to Dashboard
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Lock size={32} />
            <Typography variant="h4" component="h1">
              Change Password
            </Typography>
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Update your account password. Make sure to use a strong password that you haven't used before.
          </Typography>

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Password changed successfully! Redirecting to dashboard...
            </Alert>
          )}

          {errors.general && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.general}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Current Password"
              type="password"
              variant="outlined"
              margin="normal"
              fullWidth
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              error={!!errors.currentPassword}
              helperText={errors.currentPassword}
              disabled={loading || success}
              autoComplete="current-password"
            />

            <TextField
              label="New Password"
              type="password"
              variant="outlined"
              margin="normal"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={!!errors.newPassword}
              helperText={errors.newPassword || 'Must be at least 6 characters'}
              disabled={loading || success}
              autoComplete="new-password"
            />

            <TextField
              label="Confirm New Password"
              type="password"
              variant="outlined"
              margin="normal"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              disabled={loading || success}
              autoComplete="new-password"
            />

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || success}
                startIcon={loading ? null : <Lock size={16} />}
                sx={{ flex: 1 }}
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </Button>
              
              <Button
                type="button"
                variant="outlined"
                onClick={handleClear}
                disabled={loading || success}
              >
                Clear
              </Button>
            </Box>
          </Box>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.dark">
              <strong>Password Requirements:</strong>
              <br />
              • At least 6 characters long
              <br />
              • Must be different from your current password
              <br />
              • Use a combination of letters, numbers, and symbols for better security
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ChangePassword;